import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import {
  CustomButton,
  CustomIconButton,
  ReskinningAutocomplete,
  ReskinningInputs,
} from "../../../../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { Box, Divider, Paper, Typography } from "@material-ui/core";
import { CloseXIcon } from "../../../../../../../../assets/icons";
import {
  CreateCompanyFinance,
  ViewFinanceCorporate,
} from "../../../../../../../../Services";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";

// styles
import useStyles from "./styles";

const validationSchema = Yup.object().shape({
  companyName: Yup.string().required("Company name is required"),
  termOfLoanYears: Yup.number()
    .required("Term of loan is required")
    .min(1, "Term of loan must be at least 1 year"),
  downPaymentPercentage: Yup.number()
    .required("Down payment is required")
    .min(1, "Down payment cannot be negative or zero")
    .max(100, "Down payment cannot exceed 100%"),
  interestRate: Yup.number()
    .required("Interest rate is required")
    .min(1, " Interest rate cannot be negative or zero"),
});
const AddFinancePlanDialog = ({ onClose, reloadData, propretyId }) => {
  const styles = useStyles();
  const timerRef = useRef();

  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    pageIndex: 1,
    pageSize: 25,
    search: "",
  });
  const [financeCorporations, setFinanceCorporations] = useState({
    result: [],
    totalCount: 0,
  });
  const initialValues = {
    company: {},
    companyId: null,
    companyName: "",
    termOfLoanYears: "",
    downPaymentPercentage: "",
    interestRate: "",
    remarks: "",
    propretyId: propretyId,
  };

  const getAllFinanceCorporations = async (search) => {
    setIsLoading(true);
    try {
      const params = {
        ...filter,
        search: search,
      };
      const res = await ViewFinanceCorporate(params);
      if (!(res && res.status && res.status !== 200)) {
        setFinanceCorporations({
          result: res?.result || [],
          totalCount: res?.totalCount || 0,
        });
      }
      setIsLoading(false);
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("some-thing-went-wrong"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      const Body = {
        company: values.company || {},
        companyId: values.companyId || null,
        companyName: values.companyName || "",
        termOfLoanYears: Number(values.termOfLoanYears || 0),
        interestRate: Number(values.interestRate || 0),
        downPaymentPercentage: Number(values.downPaymentPercentage || 0),
        remarks: values.remarks || "",
        propertyId: Number(values.propretyId),
      };
      const res = await CreateCompanyFinance(Body);
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("finance-plan-added-successfully"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        onClose();
        reloadData();
      } else {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("failed-to-add-finance-plan"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("some-thing-went-wrong"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const searchHandler = (e) => {
    const { value } = e.target;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setFilter((prev) => ({ ...prev, search: value }));
      getAllFinanceCorporations(value);
    }, 700);
  };

  useEffect(() => {
    if (propretyId) {
      getAllFinanceCorporations();
    }
  }, []);

  return (
      <Paper className={styles.modal}>
        <Box className={styles.header}>
          <Box className={styles.containerTitlePlusBackIcon}>
            <Typography variant="h6" className={styles.mainTitle}>
              {translate("add-new-finance-plan")}
            </Typography>
            <CustomIconButton
              variant="text"
              size="none"
              boxShadow="none"
              color="tertiaryColor"
              hideHoverBg
              onClick={onClose}
            >
              <CloseXIcon
                width="24"
                height="24"
                fill={palette.foreground.foreground}
              />
            </CustomIconButton>
          </Box>
          <Typography variant="h6" className={styles.subTitle}>
            {translate(
              "use-this-form-to-add-a-new-finance-plan-to-the-property"
            )}
          </Typography>
        </Box>
        <Box className={styles.wrapperContent}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleSubmit, setFieldValue }) => (
              <Form
                onSubmit={handleSubmit}
                className={styles.containerFormAddFinancePlan}
              >
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("company-name")}
                  </Typography>
                  <ReskinningAutocomplete
                    containerClasses={styles.autocompleteWidthOverride}
                    rootCustomClasses={styles.autocompleteWidthOverride}
                    inputPlaceholder={`Select`}
                    idRef="activitiesSearchRef"
                    data={financeCorporations.result || []}
                    onInputKeyUp={(e) => searchHandler(e)}
                    isLoading={isLoading}
                    displayLabel={(option) =>
                      option?.contact?.company_name ?? ""
                    }
                    multiple={false}
                    onChange={(e, newValue) => {
                      setFieldValue("companyId", newValue?.contactsId);
                      setFieldValue(
                        "companyName",
                        newValue.contact?.company_name || ""
                      );
                      setFieldValue("company", newValue);
                    }}
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                    isWithError={
                      touched.companyName && Boolean(errors.companyName)
                    }
                    withoutSearchButton
                  />
                </Box>
                <Divider />
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("term-of-loan")}
                  </Typography>
                  <ReskinningInputs
                    inputPlaceholder={translate("enter-years")}
                    style={{ maxWidth: "100%" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={values.termOfLoanYears}
                    onInputChanged={(e) => {
                      setFieldValue("termOfLoanYears", e.target.value);
                    }}
                    error={
                      touched.termOfLoanYears && Boolean(errors.termOfLoanYears)
                    }
                    helperText={
                      touched.termOfLoanYears && errors.termOfLoanYears
                    }
                  />
                </Box>
                <Divider />
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("down-payment")}
                  </Typography>
                  <ReskinningInputs
                    inputPlaceholder={`% ${translate("enter")}`}
                    style={{ maxWidth: "100%" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={values.downPaymentPercentage}
                    onInputChanged={(e) => {
                      setFieldValue("downPaymentPercentage", e.target.value);
                    }}
                    error={
                      touched.downPaymentPercentage &&
                      Boolean(errors.downPaymentPercentage)
                    }
                    helperText={
                      touched.downPaymentPercentage &&
                      errors.downPaymentPercentage
                    }
                  />
                </Box>
                <Divider />
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("interest-rate")}
                  </Typography>
                  <ReskinningInputs
                    inputPlaceholder={`% ${translate("enter")}`}
                    style={{ maxWidth: "100%" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={values.interestRate}
                    onInputChanged={(e) => {
                      setFieldValue("interestRate", e.target.value);
                    }}
                    error={touched.interestRate && Boolean(errors.interestRate)}
                    helperText={touched.interestRate && errors.interestRate}
                  />
                </Box>
                <Divider />
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("remarks")}
                  </Typography>
                  <ReskinningInputs
                    inputPlaceholder={translate("enter")}
                    style={{ maxWidth: "100%" }}
                    value={values.remarks}
                    rows={4}
                    rowsMax={10}
                    multiline
                    idRef="activitiesSearchRef"
                    onInputChanged={(e) => {
                      setFieldValue("remarks", e.target.value);
                    }}
                  />
                </Box>
                {/* footer action */}
                <Box className={styles.footerActions}>
                  <CustomButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                  >
                    {translate("cancel")}
                  </CustomButton>
                  <CustomButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                  >
                    {translate("add")}
                  </CustomButton>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Paper>
  );
};

export default AddFinancePlanDialog;
